<template>
  <div class="py-4 container-fluid">
    <ToastPrime position="bottom-left" />
    <div class="card">
      <div
        class="card-header bg-white py-3 d-flex flex-wrap align-items-center justify-content-between gap-3 border-bottom">
        <div class="d-flex align-items-center gap-2">
          <h5 class="mb-0 fw-bold">{{ serviceMode ? 'Service Request List' : 'Material Request List' }}</h5>
        </div>
        <div></div>
        <div class="d-flex gap-2">
          <button type="button" class="mx-1 mb-0 btn btn-sm btn-outline-primary" @click="openFilterDialog">
            Filter
          </button>
        </div>
      </div>

      <div style="height: 500px; overflow-y: auto; overflow-x: auto;">
        <DataTable :value="table.onLoad ? dummyRows : table.data" removable-sort scrollable scroll-height="flex" table-style="min-width: 115rem" :row-class="() => table.onLoad ? '' : 'cursor-pointer'" class="text-sm" @row-click="!table.onLoad && onRowClick($event)">
          <Column field="mcr_number" header="MCR Number" header-class="bg-light text-dark text-center" sortable :style="{ width: '160px' }">
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data && slotProps.data.mcr_number ? slotProps.data.mcr_number : '' }}
            </template>
          </Column>
          <Column field="subject" header="Subject" header-class="bg-light text-dark text-center" sortable :style="{ width: '280px' }">
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data && slotProps.data.subject ? slotProps.data.subject : '' }}
            </template>
          </Column>
          <Column field="type" header="Type" header-class="bg-light text-dark text-center" sortable :style="{ width: '170px' }">
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              {{ formatType(slotProps.data.type, slotProps.data.type_detail) }}
            </template>
          </Column>
          <Column field="status" header="Status" header-class="bg-light text-dark text-center" body-class="text-center" sortable :style="{ width: '130px' }">
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              <span v-if="slotProps.data && slotProps.data.status" class="badge bg-secondary">{{ slotProps.data.status }}</span>
            </template>
          </Column>
          <Column header="Items" header-class="bg-light text-dark text-center" :style="{ width: '110px' }">
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              {{ itemCountLabel(slotProps.data) }}
            </template>
          </Column>
          <Column header="Current Position" header-class="bg-light text-dark text-center" :style="{ width: '220px' }">
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              {{ currentPositionLabel(slotProps.data) }}
            </template>
          </Column>
          <Column header="Requested By" header-class="bg-light text-dark text-center" :style="{ width: '180px' }">
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data?.user_created?.name || '' }}
            </template>
          </Column>
          <Column v-if="showDepartmentSectionColumns" header="Department" header-class="bg-light text-dark text-center" :style="{ width: '170px' }">
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data?.section }}
            </template>
          </Column>
          <Column v-if="showDepartmentSectionColumns" header="Section" header-class="bg-light text-dark text-center" :style="{ width: '200px' }">
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data?.section_detail?.description || '' }}
            </template>
          </Column>
          <Column field="created_at" header="Created At" header-class="bg-light text-dark text-center" sortable :style="{ width: '150px' }">
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data && slotProps.data.created_at ? formatDate(slotProps.data.created_at) : '' }}
            </template>
          </Column>
          <Column header="Actions" header-class="bg-light text-center" body-class="text-center" :style="{ width: '100px' }">
            <template #body="slotProps">
              <template v-if="table.onLoad">
                <Skeleton></Skeleton>
              </template>
              <template v-else>
                <a href="#" class="text-primary" @click.stop.prevent="openTracking(slotProps.data)">
                  <i class="fa fa-search-location" style="font-size: 1.2rem"></i>
                </a>
              </template>
            </template>
          </Column>
          <template #empty>
            <div style="text-align: center; padding: 40px 0;">
              <img src="/no_data.png" alt="No Data" style="max-width: 330px; opacity: 0.7; margin-bottom: 0;" />
              <div style="color: #888; font-size: 1.1em; font-weight: 500;">No Data</div>
            </div>
          </template>
        </DataTable>
      </div>
      <div class="card-footer pb-0 d-flex justify-content-between align-items-center">
        <BasePagination v-model="table.page.current_pages" :page-count="table.page.total_pages || 1" size="sm"
          @click="changePage()" />
        <p>
          {{ (table.page.current_pages - 1) * table.page.per_pages + table.data.length }} of {{ table.total }}
        </p>
      </div>
    </div>

    <!-- Dialog Tracking -->
    <DialogPrime v-model:visible="trackingModal.visible" modal :show-header="false" style="width: 50rem; margin: 10px;" @show="onTrackingShow">
      <Tracking ref="trackingRef" @close="trackingModal.visible = false" />
    </DialogPrime>

    <!-- Dialog Filter (PrimeVue) -->
    <DialogPrime v-model:visible="filterDialog.visible" modal style="width: 30rem; margin: 10px;"
      class="mcr-filter-modal">
      <template #header>
        <div class="d-flex align-items-center gap-3">
          <div>
            <h5 class="mb-0 fw-bold text-dark">Filter</h5>
          </div>
        </div>
      </template>
      <div class="row g-4">
        <div class="col-12">
          <div class="row g-3">
            <div class="col-12">
              <label for="filter-type" class="form-label fw-semibold">Type</label>
              <MultiSelect id="filter-type" v-model="filterDialog.form.type" :options="typeOptions" option-label="label"
                option-value="value" placeholder="All Types" class="w-100" display="chip" :loading="typeLoading" @show="loadTypeOptions" />
            </div>
            <div class="col-12">
              <label for="filter-subject" class="form-label fw-semibold">Subject</label>
              <InputText id="filter-subject" v-model="filterDialog.form.subject" placeholder="Subject" fluid />
            </div>
            <div class="col-12">
              <label for="filter-departement" class="form-label fw-semibold">Departement</label>
              <MultiSelect id="filter-departement" v-model="filterDialog.form.section" :options="sectionOptions" option-label="label"
                option-value="value" filter placeholder="Search Departement (min. 2 characters)" class="w-100" display="chip" show-clear
                :loading="sectionLoading" @show="loadSectionOptions" @filter="searchSectionOptions" />
            </div>
            <div class="col-12">
              <label for="filter-section" class="form-label fw-semibold">Section</label>
              <MultiSelect id="filter-section" v-model="filterDialog.form.sub_section" :options="subSectionOptions" option-label="label"
                option-value="value" filter placeholder="Search Section (min. 2 characters)" class="w-100" display="chip" show-clear
                :loading="subSectionLoading" @show="loadSubSectionOptions" @filter="searchSubSectionOptions" />
            </div>
            <div class="col-12" v-if="userCompany === 'MIND-ID'">
              <label for="filter-company" class="form-label fw-semibold">Company</label>
              <MultiSelect id="filter-company" v-model="filterDialog.form.company" :options="companyOptions" option-label="label"
                option-value="value" placeholder="All Companies" class="w-100" display="chip" />
            </div>
            <div class="col-12">
              <label for="filter-creator" class="form-label fw-semibold">Creator</label>
              <MultiSelect id="filter-creator" v-model="filterDialog.form.created_by" :options="creatorOptions" option-label="label"
                option-value="namecode" filter placeholder="Search Creator (min. 2 characters)" class="w-100" display="chip"
                @filter="searchCreatorOptions" />
            </div>
            <div class="col-12">
              <label for="filter-created-at" class="form-label fw-semibold">Created At</label>
              <Calendar id="filter-created-at" v-model="filterDialog.form.created_at_date" :show-icon="true"
                placeholder="YYYY-MM-DD" class="w-100" />
            </div>
          </div>
        </div>
      </div>
      <template #footer>
        <div class="d-flex justify-content-end gap-2 pt-3 border-top w-100 mt-2 px-1">
          <ButtonPrime type="button" label="Reset" severity="danger" raised size="small" class="px-4 border-0"
            @click="resetFilter()" />
          <ButtonPrime type="button" severity="contrast" raised size="small" label="Apply Filter" class="px-4"
            @click="applyFilter()" />
        </div>
      </template>
    </DialogPrime>
  </div>
</template>

<script>
import BasePagination from "@/components/BasePagination.vue";
import Tracking from "@/components/Tracking.vue";
import api from "@/helpers/api";
import requestListService from "@/services/materialCatalogingRequest/requestListService";
import formConfigService from "@/services/materialCatalogingRequest/formConfigService";
import companiesService from "@/services/generalMasterData/companiesService";
import generalMasterDataService from "@/services/generalMasterData/generalMasterDataService";
import authService from "@/services/authService";
import manageAccountService from "@/services/manageAccount/manageAccountService";
import moment from "moment";

// Field filter yang opsinya dimuat saat dropdown dibuka -> nama state daftar opsinya, supaya default dari profil
// (`list_filter_defaults`) tetap tampil sebagai chip sebelum opsinya dimuat (ensureSelectedOption).
const FILTER_OPTION_LISTS = { section: 'sectionOptions', sub_section: 'subSectionOptions' };

export default {
  components: { BasePagination, Tracking },
  props: {
    serviceMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      table: {
        onLoad: true,
        total: 0,
        data: [],
        limit: 100,
        page: {
          current_pages: 1,
          per_pages: 10,
          total_pages: 1,
        },
        loadTimeout: null,
      },
      dummyRows: Array.from({ length: 10 }, () => ({})),
      modal: {
        visible: false,
        action: "",
        title: "",
        data: { subject: '', type: 'choose_type' },
        errorMessage: {},
        onLoading: false,
      },
      filterDialog: {
        visible: false,
        form: {
          type: this.$route.query.type ? this.$route.query.type.split(',') : [],
          subject: this.$route.query.subject || '',
          section: this.$route.query.section ? this.$route.query.section.split(',') : [],
          sub_section: this.$route.query.sub_section ? this.$route.query.sub_section.split(',') : [],
          status: this.$route.query.status ? this.$route.query.status.split(',') : [],
          company: this.$route.query.company ? this.$route.query.company.split(',') : [],
          created_by: this.$route.query.created_by ? this.$route.query.created_by.split(',') : [],
          created_at_date: this.$route.query.created_at ? new Date(this.$route.query.created_at) : null,
        }
      },
      creatorOptions: [],
      sectionOptions: [],
      subSectionOptions: [],
      allSectionOptions: [],
      allSubSectionOptions: [],
      sectionLoading: false,
      subSectionLoading: false,
      typeLoading: false,
      typeOptions: [],
      statusOptions: [
        { label: 'Draft', value: 'draft' },
        { label: 'Processed', value: 'processed' },
        { label: 'Catalogued', value: 'catalogued' },
        { label: 'SAP', value: 'sap' },
        { label: 'Closed', value: 'closed' },
        { label: 'Rejected', value: 'rejected' },
      ],
      companyOptions: [],
      typeMap: {},
      trackingModal: {
        visible: false,
        mcrCode: '',
      },
    };
  },
  computed: {
    userCompany() {
      const userAuth = JSON.parse(localStorage.getItem('userAuth') || '{}');
      const companyCode = userAuth?.user?.company_code || '';
      return companyCode;
    },
    // Kolom Department & Section tidak ditampilkan untuk ANTAM dan TIMAH.
    showDepartmentSectionColumns() {
      return !['ANTAM', 'TIMAH'].includes(this.userCompany);
    },
    clientCode() {
      const userAuth = JSON.parse(localStorage.getItem('userAuth') || '{}');
      return userAuth?.user?.client_code || userAuth?.user?.company_code || '';
    }
  },
  async mounted() {
    await this.init();
  },
  watch: {
    // Route "Material Request List" & "Service Request List" memakai komponen yang sama,
    // hanya beda prop serviceMode. Vue me-reuse instance sehingga mounted() tidak jalan lagi.
    // Watcher ini me-reset state & fetch ulang ketika berpindah antar kedua menu tersebut.
    serviceMode() {
      this.resetStateForModeChange();
      this.init();
    },
  },
  methods: {
    resetStateForModeChange() {
      this.table.onLoad = true;
      this.table.data = [];
      this.table.total = 0;
      this.table.page.current_pages = 1;
      this.table.page.total_pages = 1;
      this.filterDialog.form = { type: [], subject: '', status: [], company: [], created_by: [], section: [], sub_section: [], created_at_date: null };
      this.creatorOptions = [];
      this.sectionOptions = [];
      this.subSectionOptions = [];
      this.allSectionOptions = [];
      this.allSubSectionOptions = [];
      this.sectionLoading = false;
      this.subSectionLoading = false;
      this.typeLoading = false;
      this.typeOptions = [];
      this.typeMap = {};
    },
    async init() {
      if (this.userCompany === 'MIND-ID') {
        this.fetchCompanyOptions();
      }
      // filter automastion: ambil section user
      await this.fetchProfileDefaults();
      this.get();
    },
    // Default filter dari profil user, diatur GeneralSetting `list_filter_defaults` (bukan hardcode per company/role).
    // Baris setting: { company, page (nama route / '*'), role?, exclude_role?, filters: { field_filter: [path profil, ...] } }.
    // Baris pertama yang cocok dengan company + halaman + role user dipakai; tidak ada yang cocok -> tanpa default.
    // Tiap field filter diisi dari path profil PERTAMA yang terisi (nilai tunggal / array), kecuali field itu sudah ada
    // di route query atau sudah terisi.
    async fetchProfileDefaults() {
      const query = this.$route.query;
      try {
        const rule = await this.resolveFilterDefaultRule();
        if (!rule) return;
        const response = await api(this, authService.getNavbarProfile(), false);
        const profile = response?.data;
        if (!profile) return;
        const form = this.filterDialog.form;
        Object.entries(rule.filters || {}).forEach(([field, paths]) => {
          if (query[field] || !(field in form)) return;
          const values = this.profileValues(profile, paths);
          if (!values.length) return;
          if (Array.isArray(form[field])) {
            if (form[field].length) return;
            form[field] = values;
            // Tampilkan default sebagai chip tanpa memanggil API (opsi baru dimuat saat dropdown dibuka).
            const options = FILTER_OPTION_LISTS[field];
            if (options) values.forEach((v) => this.ensureSelectedOption(this[options], v));
          } else if (!form[field]) {
            form[field] = values[0];
          }
        });
      } catch (error) {
        console.error('Failed to fetch profile defaults:', error);
      }
    },
    async resolveFilterDefaultRule() {
      const response = await api(this, formConfigService.get('list_filter_defaults'), false);
      const rows = Array.isArray(response?.data?.data) ? response.data.data : [];
      const lower = (list) => [].concat(list ?? []).map((v) => String(v).toLowerCase());
      const roles = lower(this.$store.state.auth?.userHasRoles);
      const page = this.$route.name;
      return rows.find((row) => {
        if (row.company !== this.userCompany) return false;
        const pages = [].concat(row.page ?? '*');
        if (!pages.includes('*') && !pages.includes(page)) return false;
        const allowed = lower(row.role ?? '*');
        if (!allowed.includes('*') && !allowed.some((r) => roles.includes(r))) return false;
        return !lower(row.exclude_role).some((r) => roles.includes(r));
      }) || null;
    },
    // Nilai path profil PERTAMA yang terisi, selalu dikembalikan sebagai array (path boleh dot-path, nilai tunggal / array).
    profileValues(profile, paths) {
      for (const path of [].concat(paths || [])) {
        const value = String(path).split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), profile);
        const values = [].concat(value ?? []).filter((v) => v !== null && v !== undefined && v !== '');
        if (values.length) return values;
      }
      return [];
    },
    async fetchCompanyOptions() {
      try {
        const response = await api(this, companiesService.list({ limit: 0 }), false);
        const items = response?.data?.data || response?.data || [];
        this.companyOptions = Array.isArray(items) ? items.map(item => ({
          label: item.description ? `${item.code} - ${item.description}` : item.code,
          value: item.code,
        })) : [];
      } catch (error) {
        console.error('Failed to fetch company options:', error);
      }
    },
    // `typeDetail` = `type_detail` dari response request-list (code + description dari GeneralSetting mcr_type);
    // tanpa itu (response lama) fallback ke opsi dropdown Type / peta lama.
    formatType(type, typeDetail) {
      if (!type) return "";
      if (typeDetail && typeDetail.description) return `${typeDetail.code} - ${typeDetail.description}`;
      const option = this.typeOptions.find(opt => opt.value === type);
      if (option) return option.label;

      const map = { 'mrp': 'UPDATE MRP', 'create-new': 'CREATE-NEW', 'flag-unflag': 'FLAG-UNFLAG', 'change-material': 'CHANGE-MATERIAL', 'block-unblock': 'BLOCK-UNBLOCK', 'extend': 'EXTEND', 'update-price': 'UPDATE-PRICE', 'mass-update-mrp': 'MASS UPDATE MRP', 'mcr007': 'MASS UPDATE MRP' };
      return map[type] || map[String(type).toLowerCase()] || type;
    },
    formatDate(dateString) {
      if (!dateString) return "";
      return moment(dateString).format("DD/MM/YYYY HH:mm");
    },
    itemCountLabel(data) {
      const count = data?.emcr_item_count || 0;
      return `${count} ${count === 1 ? 'item' : 'items'}`;
    },
    currentPositionLabel(data) {
      const receivers = data?.task_ticket?.approval_criteria;
      if (!Array.isArray(receivers)) return '';
      const roleReceiver = receivers.find((r) => r?.to === 'role');
      if (!roleReceiver || !Array.isArray(roleReceiver.values)) return '';
      return roleReceiver.values.join(', ');
    },
    openAddModal() {
      this.modal.visible = true;
      this.modal.title = 'Add MCR Draft';
      this.modal.data = { subject: '', type: '' };
      this.modal.errorMessage = {};
      this.modal.onLoading = false;
    },
    closeModal() {
      this.modal.visible = false;
      this.modal.data = { subject: '', type: '' };
      this.modal.errorMessage = {};
      this.modal.onLoading = false;
    },
    openFilterDialog() {
      this.filterDialog.visible = true;
    },
    resetFilter() {
      this.filterDialog.form = { type: [], subject: '', status: [], company: [], created_by: [], section: [], sub_section: [], created_at_date: null };
      this.creatorOptions = [];
    },

    mapOptions(list, query = '') {
      const options = new Map();
      (Array.isArray(list) ? list : []).forEach((item) => {
        const code = item?.code || '';
        const label = item?.description || code;
        if (code && !options.has(code)) options.set(code, { label, value: code });
      });
      let result = [...options.values()];
      if (query) result = this.filterOptionsByQuery(result, query);
      return result;
    },
    filterOptionsByQuery(options, query) {
      const q = query.toLowerCase();
      return options.filter(o => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q));
    },
    // Pastikan nilai default tetap tampil walau tidak ketemu di opsi (mis. dari profil user).
    ensureSelectedOption(options, value) {
      if (!value) return;
      if (options.some(o => o.value === value)) return;
      options.unshift({ label: value, value });
    },
    async fetchSectionOptions(search, limit) {
      this.sectionLoading = true;
      try {
        const params = { limit, client: this.clientCode };
        if (search) params.search = search;
        const res = await api(this, generalMasterDataService.list('section', params), false);
        return this.mapOptions(res?.data?.data || res?.data || [], search);
      } catch {
        return [];
      } finally {
        this.sectionLoading = false;
      }
    },
    async fetchSubSectionOptions(search, limit) {
      this.subSectionLoading = true;
      try {
        const params = { limit, client: this.clientCode };
        if (search) params.search = search;
        const res = await api(this, generalMasterDataService.list('sub-section', params), false);
        return this.mapOptions(res?.data?.data || res?.data || [], search);
      } catch {
        return [];
      } finally {
        this.subSectionLoading = false;
      }
    },
    async loadSectionOptions() {
      const fetched = await this.fetchSectionOptions('', 10);
      this.sectionOptions = Array.isArray(fetched) ? fetched : [];
      this.allSectionOptions = [...this.sectionOptions];
      if (!Array.isArray(this.filterDialog.form.section)) this.filterDialog.form.section = [];
      if (!this.filterDialog.form.section.length && this.sectionOptions.length === 1) {
        this.filterDialog.form.section = [this.sectionOptions[0].value];
      }
      this.filterDialog.form.section.forEach((v) => this.ensureSelectedOption(this.sectionOptions, v));
    },
    // Label "Section" -> master sub-section. Dipanggil saat dropdown dibuka (@show), limit 10.
    async loadSubSectionOptions() {
      const fetched = await this.fetchSubSectionOptions('', 10);
      this.subSectionOptions = Array.isArray(fetched) ? fetched : [];
      this.allSubSectionOptions = [...this.subSectionOptions];
      if (!Array.isArray(this.filterDialog.form.sub_section)) this.filterDialog.form.sub_section = [];
      if (!this.filterDialog.form.sub_section.length && this.subSectionOptions.length === 1) {
        this.filterDialog.form.sub_section = [this.subSectionOptions[0].value];
      }
      this.filterDialog.form.sub_section.forEach((v) => this.ensureSelectedOption(this.subSectionOptions, v));
    },
    // Search server-side: hit API begitu ketik min. 2 karakter.
    // Opsi terpilih (bisa lebih dari satu) wajib dipertahankan agar chip MultiSelect tidak hilang.
    async searchSectionOptions(event) {
      const query = (event?.value || '').trim();
      const selectedValues = this.filterDialog.form.section || [];
      if (query.length < 2) {
        const base = [...this.allSectionOptions];
        selectedValues.forEach((v) => this.ensureSelectedOption(base, v));
        this.sectionOptions = base;
        return;
      }
      let options = await this.fetchSectionOptions(query, 10);
      // Kalau backend abaikan param `search` / balik kosong, saring daftar yang sudah dimuat.
      if (!options.length) options = this.filterOptionsByQuery(this.allSectionOptions, query);
      const optionValues = new Set(options.map((o) => o.value));
      const missingSelected = this.sectionOptions.filter((o) => selectedValues.includes(o.value) && !optionValues.has(o.value));
      this.sectionOptions = [...missingSelected, ...options];
    },
    // Opsi terpilih (bisa lebih dari satu) wajib dipertahankan agar chip MultiSelect tidak hilang.
    async searchSubSectionOptions(event) {
      const query = (event?.value || '').trim();
      const selectedValues = this.filterDialog.form.sub_section || [];
      if (query.length < 2) {
        const base = [...this.allSubSectionOptions];
        selectedValues.forEach((v) => this.ensureSelectedOption(base, v));
        this.subSectionOptions = base;
        return;
      }
      let options = await this.fetchSubSectionOptions(query, 10);
      // Kalau backend abaikan param `search` / balik kosong, saring daftar yang sudah dimuat.
      if (!options.length) options = this.filterOptionsByQuery(this.allSubSectionOptions, query);
      const optionValues = new Set(options.map((o) => o.value));
      const missingSelected = this.subSectionOptions.filter((o) => selectedValues.includes(o.value) && !optionValues.has(o.value));
      this.subSectionOptions = [...missingSelected, ...options];
    },
    async searchCreatorOptions(event) {
      const query = (event?.value || '').trim();
      // Opsi yang sudah dipilih harus tetap ada di `creatorOptions`, walau tidak ikut hasil
      // pencarian baru (atau saat query dikosongkan) — kalau tidak, MultiSelect kehilangan
      // referensi objeknya dan label chip yang sudah dipilih ikut hilang/kosong.
      const selectedNamecodes = this.filterDialog.form.created_by || [];
      const keepSelected = this.creatorOptions.filter((o) => selectedNamecodes.includes(o.namecode));
      if (query.length < 2) { this.creatorOptions = keepSelected; return; }
      try {
        const companyParam = this.userCompany === 'MIND-ID'
          ? { company_code: this.filterDialog.form.company }
          : { company_code: this.userCompany };
        const response = await api(this, manageAccountService.list({ ...companyParam, search: query, limit: 10 }), false);
        const list = response?.data?.data || response?.data || [];
        const fetched = (Array.isArray(list) ? list : []).map((u) => ({
          namecode: u.namecode,
          username: u.username,
          name: u.name,
          label: u.name ? `${u.username} - ${u.name}` : u.username,
        }));
        const fetchedNamecodes = new Set(fetched.map((o) => o.namecode));
        this.creatorOptions = [...keepSelected.filter((o) => !fetchedNamecodes.has(o.namecode)), ...fetched];
      } catch (error) {
        this.creatorOptions = keepSelected;
      }
    },
    buildParams() {
      const form = this.filterDialog.form;
      ['type', 'status', 'company', 'created_by', 'section', 'sub_section'].forEach((k) => {
        if (!Array.isArray(form[k])) form[k] = [];
      });
      if (this.userCompany !== 'MIND-ID') {
        form.company = [this.userCompany];
      }
      form.status = ["send_back", "processed", "rejected", "sap"];
      const params = {
        limit: this.table.limit,
        page: this.table.page.current_pages,
      };
      if (form.subject) params.subject = form.subject;
      if (form.type.length) {
        params.type = form.type;
      } else if (this.typeOptions.length) {
        // typeOptions baru terisi setelah dropdown Type dibuka; kirim semua type hanya jika sudah ada.
        params.type = this.typeOptions.map(o => o.value);
      }
      if (form.status.length) params.status = form.status;
      if (form.company.length) params.company = form.company;
      if (form.created_by.length) params.created_by = form.created_by;
      if (form.section.length) params.section = form.section;
      if (form.sub_section.length) params.sub_section = form.sub_section;
      if (form.created_at_date) params.created_at = moment(form.created_at_date).format('YYYY-MM-DD');
      return params;
    },
    applyFilter() {
      // MultiSelect show-clear bisa meng-set model ke null; normalisasi dulu.
      const form = this.filterDialog.form;
      ['type', 'status', 'company', 'created_by', 'section', 'sub_section'].forEach((k) => {
        if (!Array.isArray(form[k])) form[k] = [];
      });
      // Sinkronkan ke query dan refresh data
      const query = {
        ...(form.type.length ? { type: form.type.join(',') } : {}),
        ...(form.subject ? { subject: form.subject } : {}),
        ...(form.status.length ? { status: form.status.join(',') } : {}),
        ...(form.company.length ? { company: form.company.join(',') } : {}),
        ...(form.created_by.length ? { created_by: form.created_by.join(',') } : {}),
        ...(form.section.length ? { section: form.section.join(',') } : {}),
        ...(form.sub_section.length ? { sub_section: form.sub_section.join(',') } : {}),
        ...(form.created_at_date ? { created_at: moment(form.created_at_date).format('YYYY-MM-DD') } : {}),
      };
      this.$router.push({ query: Object.keys(query).length ? query : null });
      this.table.page.current_pages = 1;
      this.filterDialog.visible = false;
      this.get(this.buildParams());
    },
    openEditModal(data) {
      if (!data) return;
      this.getDetailsAndNavigate(data);
    },
    onRowClick(event) {
      if (!event.data || !event.data.id) return;
      this.goToForm(event.data);
    },
    goToForm(data) {
      if (!data || !data.id) return;
      const route = this.$router.resolve({
        name: 'MCR Form for Material Request',
        params: {
          id: data.id,
          action: "edit",
          mcrCode: data.mcr_code || ''
        }
      });
      this.$router.push(route.href);
    },
    openTracking(data) {
      this.trackingModal.mcrCode = data.mcr_code;
      this.trackingModal.visible = true;
    },
    // Dipanggil setelah DialogPrime selesai mount kontennya (ref sudah siap)
    onTrackingShow() {
      this.$refs.trackingRef?.getTracking({
        type: ['start', 'approval'],
        code: 'MCR',
        key: this.trackingModal.mcrCode,
        result: ['success', 'approve', 'send_back', 'reject'],
      });
    },
    async getDetailsAndNavigate(data) {
      try {
        this.$router.push({ name: 'MCR Form for Material Request', params: { id: data.id, action: "edit", mcrCode: data.mcr_code } });
      } catch (error) {
        console.error('Error navigating to UserForm:', error);
        this.$toastNotifPrime('error', 'Error', 'Failed to navigate to UserForm');
      }
    },
    // Dipanggil saat dropdown Type dibuka (@show), bukan saat list dimuat. Hasilnya di-cache.
    async loadTypeOptions() {
      if (this.typeOptions.length) return;
      this.typeLoading = true;
      try {
        const response = await api(this, formConfigService.get('mcr_type'), false);
        const types = response?.data?.data ?? [];
        if (Array.isArray(types)) {
          // Filter based on mode: material (MCR001-MCR007) or service (MCR008-MCR009)
          const filteredTypes = types.filter(t => {
            const codeNum = parseInt(t.code?.replace('MCR', ''), 10);
            return this.serviceMode ? (codeNum >= 8 && codeNum <= 9) : (codeNum >= 1 && codeNum <= 7);
          });
          const options = filteredTypes.map(t => ({
            label: `${t.code} - ${t.description}`,
            value: t.code
          }));
          const map = {};
          filteredTypes.forEach(t => { map[t.code] = `${t.code} - ${t.description}`; });
          this.typeOptions = options;
          this.typeMap = map;
        }
      } catch (e) {
        this.typeOptions = [];
        this.typeMap = {};
      } finally {
        this.typeLoading = false;
      }
    },
    async get(paramsOverride) {
      this.table.onLoad = true;
      try {
        const response = await api( this, requestListService.list(paramsOverride ?? this.buildParams()), false );
        const root = response?.data ?? null;
        let rows = [];
        let pagedMeta = {};

        if (Array.isArray(root)) {
          rows = root;
        } else if (root && typeof root === 'object' && Array.isArray(root.data)) {
          rows = root.data;
          pagedMeta = root;
        } else if (root && typeof root === 'object') {
          rows = [root];
        }

        this.table.data = Array.isArray(rows) ? rows : [];
        this.table.total = (typeof pagedMeta.total === 'number') ? pagedMeta.total : this.table.data.length;
        this.table.page.total_pages = (typeof pagedMeta.last_page === 'number') ? pagedMeta.last_page : 1;
        this.table.page.current_pages = (typeof pagedMeta.current_page === 'number') ? pagedMeta.current_page : 1;
        this.table.page.per_pages = (typeof pagedMeta.per_page === 'number') ? pagedMeta.per_page : this.table.limit;
      } catch (error) {
        this.table.data = [];
        this.table.total = 0;
        this.table.page.total_pages = 1;
      } finally {
        this.table.onLoad = false;
      }
    },
    changePage() {
      this.get();
    },
  },
};
</script>

<style scoped>
::v-deep(.p-datatable-thead > tr > th .p-column-header-content) {
  justify-content: center;
}

::v-deep(.cursor-pointer) {
  cursor: pointer;
}

::v-deep(.cursor-pointer:hover) {
  background-color: #f8f9fa;
}
</style>